import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, Users, Mail, Target, AlertCircle, CheckCircle, Zap, Calendar } from 'lucide-react';


const MarketingDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/marketing/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching marketing data:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  const sendCampaign = async (type, segment) => {
    try {
      const response = await axios.post(`/api/marketing/campaign/send`, {
        type,
        segment
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Failed to send campaign');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!data) return null;

  const { learning_mode, analytics, customer_segments, recommendations } = data;
  const analytics7d = analytics?.last_7_days || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Marketing Automation</h2>
          <p className="text-gray-400 text-sm">Grow your studio bookings on autopilot</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Learning Mode Banner */}
      {learning_mode?.enabled && (
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="text-blue-400" size={24} />
            <div className="flex-1">
              <p className="text-white font-semibold">🎓 Learning Mode Active</p>
              <p className="text-blue-300 text-sm">
                Day {learning_mode.days_completed} of 30 - Collecting baseline data before activating auto-triggers
              </p>
            </div>
            <div className="bg-blue-900/50 px-4 py-2 rounded-lg">
              <p className="text-blue-300 text-xs">Progress</p>
              <p className="text-white text-2xl font-bold">{Math.round((learning_mode.days_completed / 30) * 100)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-900/40 to-green-800/40 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-300 text-sm">Avg Hours/Day</p>
              <p className="text-white text-2xl font-bold">{analytics7d.avg_hours_per_day || 0}</p>
              <p className="text-green-400 text-xs mt-1">Last 7 days</p>
            </div>
            <TrendingUp className="text-green-400" size={32} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/40 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-300 text-sm">Total Bookings</p>
              <p className="text-white text-2xl font-bold">{analytics7d.total_bookings || 0}</p>
              <p className="text-blue-400 text-xs mt-1">This week</p>
            </div>
            <Calendar className="text-blue-400" size={32} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/40 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-300 text-sm">Active Customers</p>
              <p className="text-white text-2xl font-bold">{customer_segments?.counts?.active || 0}</p>
              <p className="text-purple-400 text-xs mt-1">Engaged users</p>
            </div>
            <Users className="text-purple-400" size={32} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/40 border border-orange-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-300 text-sm">At Risk</p>
              <p className="text-white text-2xl font-bold">{customer_segments?.counts?.at_risk || 0}</p>
              <p className="text-orange-400 text-xs mt-1">Need attention</p>
            </div>
            <AlertCircle className="text-orange-400" size={32} />
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="bg-gray-900/50 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Zap className="text-yellow-400" size={24} />
            Smart Recommendations
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, idx) => (
              <div
                key={idx}
                className={`border rounded-lg p-4 ${
                  rec.priority === 'high'
                    ? 'bg-red-900/20 border-red-500/30'
                    : rec.priority === 'medium'
                    ? 'bg-yellow-900/20 border-yellow-500/30'
                    : 'bg-blue-900/20 border-blue-500/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          rec.priority === 'high'
                            ? 'bg-red-900/50 text-red-300'
                            : rec.priority === 'medium'
                            ? 'bg-yellow-900/50 text-yellow-300'
                            : 'bg-blue-900/50 text-blue-300'
                        }`}
                      >
                        {rec.priority.toUpperCase()}
                      </span>
                      <h4 className="text-white font-semibold">{rec.title}</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">{rec.description}</p>
                    {rec.potential_revenue > 0 && (
                      <p className="text-green-400 text-sm font-semibold">
                        💰 Potential: ${rec.potential_revenue}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const segmentMap = {
                        're_engagement': 'at_risk',
                        'conversion': 'new',
                        'loyalty': 'vip'
                      };
                      const segment = segmentMap[rec.type];
                      if (segment) {
                        sendCampaign(rec.type, segment);
                      } else {
                        toast.info('Manual action required');
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
                  >
                    Take Action
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customer Segments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-900/50 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-4">Customer Segments</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
              <div>
                <p className="text-purple-300 font-semibold">💎 VIP Customers</p>
                <p className="text-gray-400 text-sm">5+ bookings</p>
              </div>
              <span className="text-white text-2xl font-bold">{customer_segments?.counts?.vip || 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
              <div>
                <p className="text-green-300 font-semibold">✅ Active</p>
                <p className="text-gray-400 text-sm">Booked recently</p>
              </div>
              <span className="text-white text-2xl font-bold">{customer_segments?.counts?.active || 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
              <div>
                <p className="text-yellow-300 font-semibold">⚠️ At Risk</p>
                <p className="text-gray-400 text-sm">30-60 days inactive</p>
              </div>
              <span className="text-white text-2xl font-bold">{customer_segments?.counts?.at_risk || 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
              <div>
                <p className="text-red-300 font-semibold">😴 Inactive</p>
                <p className="text-gray-400 text-sm">60+ days no booking</p>
              </div>
              <span className="text-white text-2xl font-bold">{customer_segments?.counts?.inactive || 0}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <div>
                <p className="text-blue-300 font-semibold">🆕 New Users</p>
                <p className="text-gray-400 text-sm">Never booked</p>
              </div>
              <span className="text-white text-2xl font-bold">{customer_segments?.counts?.new || 0}</span>
            </div>
          </div>
        </div>

        {/* Quick Insights */}
        <div className="bg-gray-900/50 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-4">Quick Insights</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Peak Booking Day</p>
              <p className="text-white text-xl font-bold">{analytics7d.peak_day || 'N/A'}</p>
              <p className="text-purple-400 text-sm">{analytics7d.peak_day_hours || 0} hours booked</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Most Booked Engineer</p>
              <p className="text-white text-xl font-bold">{analytics7d.most_booked_engineer || 'N/A'}</p>
              <p className="text-purple-400 text-sm">{analytics7d.most_booked_count || 0} bookings</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-gray-400 text-sm mb-1">Growth Trend</p>
              <p className="text-white text-xl font-bold capitalize">{analytics7d.trend || 'Building'}</p>
              <p className="text-purple-400 text-sm">Based on recent data</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="text-blue-400 mt-1" size={20} />
          <div>
            <p className="text-blue-300 font-semibold">Marketing System Active</p>
            <p className="text-blue-400 text-sm mt-1">
              {learning_mode?.enabled 
                ? 'System is learning your patterns. Safe campaigns are running. Powerful features will activate after baseline is established.'
                : 'Full automation active. System is monitoring and optimizing your bookings.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingDashboard;